import { TransactionView } from "@bitwarden/web-vault/app/models/view/transaction/transaction.view";
import { BalanceCalculatedUtils } from "@bitwarden/web-vault/app/services/store/calculation-base/balances/balance.calculated.utils";
import { WebWorkerQueue } from "@bitwarden/web-vault/app/services//web-worker/WebWorkerQueue";
import { GroupingSelection } from "@bitwarden/web-vault/app/services/store/calculation-base/controls/group.control";
import { FilterControlType } from "@bitwarden/web-vault/app/services/store/calculation-base/controls/filter.control";
import { inject, Signal } from "@angular/core";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { PeriodType } from "@bitwarden/web-vault/app/models/types/filter.types";

export type CalculatedBalanceCollectionNames =
  | "BalanceByAccounts"
  | "BalanceByAccountsAndSymbols"
  | "BalanceByTime"
  | "BalanceByTimeAndAccounts"
  | "BalanceByTransactions"
  | "BalanceCurrent"
  | "ScenarioBalanceByAccountsAndSymbols"
  | "ScenarioBalanceByTime"
  | "ScenarioBalanceByTimeAndAccounts"
  | "ScenarioBalanceByTransactions";

export type CalculatedBalances = Map<string, number>;
export type CalculatedBalancesArray = Array<Map<string, number>>;
export type CalculatedBalancesGroupedTwice = Map<string, CalculatedBalances>;
export type CalculatedBalancesGroupedTwiceArray = Array<CalculatedBalancesGroupedTwice>;

export type CalculatedBalanceType =
  | number
  | CalculatedBalances
  | CalculatedBalancesGroupedTwice
  | CalculatedBalancesArray
  | CalculatedBalancesGroupedTwiceArray;

export type CalculatedBalanceParams = {
  webWorkerQueue?: WebWorkerQueue;
  calculatedTransactions?: TransactionView[];
  filter?: FilterControlType;
  group?: GroupingSelection;
  period?: PeriodType;
};

export type ScenarioBalanceParams = {
  webWorkerQueue?: WebWorkerQueue;
  scenarioTransactions?: TransactionView[][];
  group?: GroupingSelection;
  period?: PeriodType;
};

export abstract class CalculatedBalanceCollection extends BalanceCalculatedUtils {
  // A value, not only a type, in each subclass: staleCheck() logs it (a minified class name
  // wouldn't say which collection dropped its result).
  protected abstract readonly type: CalculatedBalanceCollectionNames;
  protected abstract calculations: CalculatedBalanceType;
  protected abstract _balances: Signal<CalculatedBalanceType>;
  abstract readonly balances: Signal<CalculatedBalanceType>;

  /** Bumped by clear(). clear() can't cancel an updateCalculations() that is already awaiting the
   * worker (e.g. logout mid-refresh), so updateCalculations() takes a staleCheck() before its
   * await and drops its result if a clear landed meanwhile, instead of repopulating the
   * just-cleared balances with the previous session's figures. */
  private epoch = 0;
  protected log: LogService = inject(LogService);

  abstract updateCalculations(parameters: CalculatedBalanceParams): Promise<void>;
  abstract getCalculations(): CalculatedBalanceType;
  abstract triggerChanges(): void;

  clear(): void {
    this.epoch++;
    this.clearCalculations();
  }

  /** Resets the calculations and balances. Called by clear(), which also invalidates in-flight
   * calculations. */
  protected abstract clearCalculations(): void;

  /** Returns a check that turns true once clear() has run since this call, and logs that the
   * calculation's result is being discarded when it does. */
  protected staleCheck(): () => boolean {
    const epoch = this.epoch;
    return () => {
      const isStale = epoch !== this.epoch;
      if (isStale) {
        this.log.info(`${this.type} finished after a clear — discarding stale result`);
      }
      return isStale;
    };
  }
}

export type CalculatedTodayStoreAdapterInterface<T extends CalculatedTodayCollectionType> = {
  [n in keyof T]: CalculatedBalanceCollection;
};

export type CalculatedPastStoreAdapterInterface<T extends CalculatedPastCollectionType> = {
  [n in keyof T]: CalculatedBalanceCollection;
};

type CalculatedTodayCollectionType = BalanceCalculatedTodayAdapterType;
type CalculatedPastCollectionType = BalanceCalculatedPastAdapterType;

export type BalanceCalculatedTodayAdapterType = {
  balanceByAccount: CalculatedBalanceCollection;
  balanceCurrent: CalculatedBalanceCollection;
};

export type BalanceCalculatedPastAdapterType = {
  balanceByTimeAndAccounts: CalculatedBalanceCollection;
  balanceByTransaction: CalculatedBalanceCollection;
  balanceByTime: CalculatedBalanceCollection;
};
